import React, { useState, useEffect, useContext, useCallback } from "react";
import { formatCurrencyExtended, formatWalletAddress, formatBalanceWithLogo, formatCurrencyWithLogo, formatCoolDownTime } from '../../utils';
import { tokenStyle, rewardStyle } from './TileStyles';
import { Card } from "@nextui-org/react";
import { Zoom, Collapse } from "@mui/material";
import ContractContext from '../../ContractContext';
import { Tooltip, IconButton } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const RewardsCard = React.memo(({ token, imageReferences, getTokenLogoUrl, poolType = "Tx", poolIndex }) => {

    const styling = rewardStyle("black");
    const { contracts } = useContext(ContractContext);
    const [rewardsAvailable, setRewardsAvailable] = useState(null);
    const [rewardsAvailableUSD, setRewardsAvailableUSD] = useState(0);
    const [rewardRate, setRewardRate] = useState(0);
    const [adjustedRewardsAvailable, setAdjustedRewardsAvailable] = useState(0);
    const [adjustedRewardsAvailableUSD, setAdjustedRewardsAvailableUSD] = useState(0);

    // Initialize isExpanded per poolIndex
    const [isExpanded, setIsExpanded] = useState(() => {
       // return localStorage.getItem(`RewardsCardIsExpanded_pool_${poolIndex}`) === 'true';
       return localStorage.getItem(`RewardsCardIsExpanded_pool`) === 'true';
    });

    const playType = poolIndex === 3 ? '🥩' : poolIndex === 4 ? '🔥' : poolIndex === 2 ? '💳' : poolIndex === 1 ? '🔄' : '💰';

    useEffect(() => {
        const getRewardsAvailable = async () => {
            if (token && contracts.floatiePlay) {
                try {
                    const rewards = await contracts.floatiePlay.methods.playPlatform(token.address, poolIndex).call();
                    setRewardsAvailable(rewards);
                } catch (error) {
                    console.error("Error fetching rewards available:", error);
                }
            }
        }

        getRewardsAvailable();
    }, [token, contracts?.floatiePlay, poolIndex]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (token && contracts.floatiePlay) {
                contracts.floatiePlay.methods.playPlatform(token.address, poolIndex).call()
                    .then(setRewardsAvailable)
                    .catch(error => console.error("Error fetching rewards available:", error));
            }
        }, 5000);
        //console.log("Interval set for rewards");
        return () => clearInterval(interval);
    }, [token, contracts?.floatiePlay, poolIndex]);

    useEffect(() => {
        if (rewardsAvailable && token) {
            const totalUSD = parseFloat(rewardsAvailable.total) * parseFloat(token.price) / (10 ** token.decimals);
            setRewardsAvailableUSD(totalUSD);

            const accuracy = parseFloat(rewardsAvailable.poolSettings.accuracy) <= parseFloat(rewardsAvailable.poolSettings.chance)
                ? 10000
                : parseFloat(rewardsAvailable.poolSettings.accuracy);
            setRewardRate(parseFloat(rewardsAvailable.poolSettings.rewardRate) / accuracy);
        }
    }, [token, rewardsAvailable]);

    useEffect(() => {
        if (rewardsAvailable && rewardRate) {
            const adjusted = (parseFloat(rewardsAvailable.total) * rewardRate).toFixed(0);
            setAdjustedRewardsAvailable(adjusted);
            const adjustedUSD = (parseFloat(rewardsAvailable.total) * rewardRate * parseFloat(token.price) / (10 ** token.decimals)).toFixed(2);
            setAdjustedRewardsAvailableUSD(adjustedUSD);
        }
    }, [rewardsAvailable, rewardRate, token]);

    const handleToggleExpand = () => {
        setIsExpanded(prevState => {
            const newState = !prevState;
            //localStorage.setItem(`RewardsCardIsExpanded_pool_${poolIndex}`, newState);
            localStorage.setItem(`RewardsCardIsExpanded_pool`, newState);
            return newState;
        });
    };

    return (
        <>
            {
                rewardsAvailable !== null && adjustedRewardsAvailableUSD > 0.009 &&
                <Zoom in={true}>
                    <div style={{ ...styling, marginTop: '20px', position: 'relative', overflow: 'hidden',
                        paddingBottom:'20px' 
                    }}>
                        {/* Toggle Button at Top Right */}
                        <IconButton
                            onClick={handleToggleExpand}
                            style={{ position: 'absolute', top: '10px', right: '10px', color: '#EC599A' }}
                            aria-label={isExpanded ? "Hide Details" : "Show Details"}
                        >
                            {isExpanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>

                        <div style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            display: 'flex',
                            width: '100%',
                            marginTop: '15px',
                            marginBottom: '10px',
                            fontSize: '18px',
                            color: '#EC599A',
                            fontWeight: 'bold',
                        }}>
                            <Tooltip title="Rewards currently available for claiming" arrow>
                                <span role="img" aria-label="rewards">{poolType} Rewards Available 🏆</span>
                            </Tooltip>
                        </div>

                        <div style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            display: 'flex',
                            gap: '5px',
                            flexDirection: 'row',
                            width: '100%',
                            fontSize: '24px',
                            fontWeight: 'bold'
                        }}>
                            {formatBalanceWithLogo(parseFloat(adjustedRewardsAvailable), token, imageReferences)} {token.label}
                        </div>
                        <div style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            display: 'flex',
                            gap: '5px',
                            flexDirection: 'row',
                            width: '100%',
                            fontWeight: 'thin'
                        }}>
                            {formatCurrencyExtended(parseFloat(adjustedRewardsAvailableUSD))}
                        </div>

                        {/* Conditionally Render Pool Info Section with Collapse */}
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <div style={subTileStyle}>
                                <div style={subTileHeaderStyle}>
                                    <Tooltip title="Information about the pool" arrow>
                                        <span role="img" aria-label="pool info"> Pool Info</span>
                                    </Tooltip>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '5px' }}>
                                    {/* Minimum Pool Amount with Tooltip */}
                                    <div style={subTileStyleCard}>
                                        <Tooltip title={`${poolType} minimum amount required to participate in the ${poolType} pool`} arrow>
                                            <span role="img" aria-label="minimum amount">
                                                {playType}<br />
                                                {formatCurrencyWithLogo(
                                                    parseFloat(rewardsAvailable.poolSettings.requiredAmount) * parseFloat(token.price) / (10 ** token.decimals),
                                                    token,
                                                    imageReferences
                                                )}
                                            </span>
                                        </Tooltip>
                                    </div>

                                    {/* Hold/Staked Amount with Tooltip */}
                                    <div style={subTileStyleCard}>
                                        <Tooltip title="Amount required to be Holding or have Staked in StakeHause to earn rewards" arrow>
                                            <span role="img" aria-label="hold amount">
                                                💼<br />
                                                {formatCurrencyWithLogo(
                                                    parseFloat(rewardsAvailable.poolSettings.minimumHoldAmount) * parseFloat(token.price) / (10 ** token.decimals),
                                                    token,
                                                    imageReferences
                                                )}
                                            </span>
                                        </Tooltip>
                                    </div>

                                    {/* Chance with Tooltip */}
                                    <div style={subTileStyleCard}>
                                        <Tooltip title="Probability of earning the reward" arrow>
                                            <span role="img" aria-label="chance">
                                                🎯<br />
                                                {(rewardsAvailable.poolSettings.chance / rewardsAvailable.poolSettings.accuracy * 100).toFixed(2)}%
                                            </span>
                                        </Tooltip>
                                    </div>

                                    {/* Cool Down with Tooltip */}
                                    <div style={subTileStyleCard}>
                                        <Tooltip title={`Time required before you can earn ${poolType} rewards again`} arrow>
                                            <span role="img" aria-label="cool down">
                                                ⏱<br />
                                                {formatCoolDownTime(rewardsAvailable.poolSettings.coolDownTime)}
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        </Collapse>

                        {/* Conditionally Render Previous Winner Section with Collapse */}
                        <Collapse in={isExpanded && parseFloat(rewardsAvailable.winnerInfo.previousWonAmount) > 0} timeout="auto" unmountOnExit>
                            <div style={subTileStyle}>
                                <div style={subTileHeaderStyle}>
                                    <Tooltip title="Details of the last winner" arrow>
                                        <span role="img" aria-label="previous winner">🏅</span>
                                    </Tooltip> Previous Winner
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '5px' }}>
                                    {/* Winner Address with Tooltip */}
                                    <div style={subTileStyleCard}>
                                        <Tooltip title="Wallet address of the previous winner" arrow>
                                            <span role="img" aria-label="address">
                                                💼<br />
                                                {formatWalletAddress(rewardsAvailable.winnerInfo.previousWinner)}
                                            </span>
                                        </Tooltip>
                                    </div>

                                    {/* Won Amount with Tooltip */}
                                    <div style={subTileStyleCard}>
                                        <Tooltip title="Rewards earned by the previous winner" arrow>
                                            <span role="img" aria-label="won amount">
                                                💰<br />
                                                {formatCurrencyWithLogo(
                                                    parseFloat(rewardsAvailable.winnerInfo.previousWonAmount) * parseFloat(token.price) / (10 ** token.decimals),
                                                    token,
                                                    imageReferences
                                                )}
                                            </span>
                                        </Tooltip>
                                    </div>

                                    {/* Win Time with Tooltip */}
                                    <div style={subTileStyleCard}>
                                        <Tooltip title="Date when the previous win occurred" arrow>
                                            <span role="img" aria-label="win time">
                                                🕒<br />
                                                {new Date(rewardsAvailable.winnerInfo.previousWinTime * 1000).toLocaleString()}
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </div>
                </Zoom>
            }
        </>
    );
});

const subTileStyle = {
    background: 'none',
    borderRadius: '8px',
    padding: '10px',
    fontSize: '14px',
    color: '#eee',
};

const subTileStyleCard = {
    background: '#1E2732',
    borderRadius: '8px',
    padding: '10px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    fontSize: '14px',
    color: '#eee',
};

const subTileHeaderStyle = {
    fontSize: '16px',
    fontWeight: 'bold',
    marginTop: '8px',
    borderBottom: '1px solid #4a525a',
    paddingBottom: '4px',
    marginBottom: '8px',
};

export default RewardsCard;
